import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Bell,
  Ticket,
  Trophy,
  Gift,
  Handshake,
  Coins,
  DollarSign,
  Building2,
  Newspaper,
  Flame,
  Clock,
  Info,
  ChevronRight,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonButton } from "../../../../Components/ui";

export const NOTIFICATION_CONFIG = {
  LotteryCreated: {
    category: "lottery",
    categoryLabel: "Lottery",
    badgeLabel: "Lottery Created",
    actionLabel: "View Lottery",
    icon: Ticket,
    iconColor: colors.primary.main,
    iconBg: colors.blue[50],
  },
  LotteryCreatedAdmin: {
    category: "lottery",
    categoryLabel: "Lottery",
    badgeLabel: "Admin Lottery",
    actionLabel: "View Lottery",
    icon: Ticket,
    iconColor: colors.primary.main,
    iconBg: colors.blue[50],
  },
  "RolloverHappened'": {
    category: "lottery",
    categoryLabel: "Lottery",
    badgeLabel: "Rollover Round",
    actionLabel: "View Rollover",
    icon: Flame,
    iconColor: colors.amber[600],
    iconBg: colors.amber[50],
  },
  RolloverHappened: {
    category: "lottery",
    categoryLabel: "Lottery",
    badgeLabel: "Rollover Round",
    actionLabel: "View Rollover",
    icon: Flame,
    iconColor: colors.amber[600],
    iconBg: colors.amber[50],
  },
  LotteryBought: {
    category: "lottery",
    categoryLabel: "Lottery",
    badgeLabel: "Ticket Purchased",
    actionLabel: "View Details",
    icon: Coins,
    iconColor: colors.emerald[600],
    iconBg: colors.emerald[50],
  },
  LotteryResult: {
    category: "winner",
    categoryLabel: "Winners & Payouts",
    badgeLabel: "Draw Concluded",
    actionLabel: "View Results",
    icon: Trophy,
    iconColor: colors.amber[600],
    iconBg: colors.amber[50],
  },
  SpinLotteryResult: {
    category: "winner",
    categoryLabel: "Winners & Payouts",
    badgeLabel: "Spin Draw Result",
    actionLabel: "View Results",
    icon: Trophy,
    iconColor: colors.amber[600],
    iconBg: colors.amber[50],
  },
  LotteryWinner: {
    category: "winner",
    categoryLabel: "Winners & Payouts",
    badgeLabel: "Winner Declared",
    actionLabel: "View Results",
    icon: Trophy,
    iconColor: colors.amber[600],
    iconBg: colors.amber[50],
  },
  WinnerPaid: {
    category: "winner",
    categoryLabel: "Winners & Payouts",
    badgeLabel: "Winner Payout",
    actionLabel: "View Payout",
    icon: DollarSign,
    iconColor: colors.emerald[600],
    iconBg: colors.emerald[50],
  },
  PartnerPaid: {
    category: "partner",
    categoryLabel: "Creators & Orgs",
    badgeLabel: "Partner Payout",
    actionLabel: "View Partner",
    icon: DollarSign,
    iconColor: colors.emerald[600],
    iconBg: colors.emerald[50],
  },
  GiftCardCreated: {
    category: "giftcard",
    categoryLabel: "Gift Cards",
    badgeLabel: "Gift Card Created",
    actionLabel: "View Gift Card",
    icon: Gift,
    iconColor: colors.purple[600],
    iconBg: colors.purple[50],
  },
  GiftCardPurchased: {
    category: "giftcard",
    categoryLabel: "Gift Cards",
    badgeLabel: "Gift Card Purchased",
    actionLabel: "View Gift Card",
    icon: Gift,
    iconColor: colors.purple[600],
    iconBg: colors.purple[50],
  },
  OrganisationAdded: {
    category: "partner",
    categoryLabel: "Creators & Orgs",
    badgeLabel: "Creator Added",
    actionLabel: "View Creators",
    icon: Building2,
    iconColor: colors.cyan[600],
    iconBg: colors.cyan[50],
  },
  PartnerCreated: {
    category: "partner",
    categoryLabel: "Creators & Orgs",
    badgeLabel: "Partner Added",
    actionLabel: "View Partners",
    icon: Handshake,
    iconColor: colors.blue[600],
    iconBg: colors.blue[50],
  },
  PlatformUpdate: {
    category: "platform",
    categoryLabel: "Platform",
    badgeLabel: "Platform Update",
    actionLabel: "Read More",
    icon: Newspaper,
    iconColor: colors.slate[600],
    iconBg: colors.slate[100],
  },
};

export const getNotificationConfig = (type) => {
  return (
    NOTIFICATION_CONFIG[type] || {
      category: "other",
      categoryLabel: "General",
      badgeLabel: type || "System Event",
      actionLabel: "View Details",
      icon: Bell,
      iconColor: colors.primary.main,
      iconBg: colors.blue[50],
    }
  );
};

const NotificationCard = ({
  notification,
  isUnread = false,
  onClick,
  onOpenDetails,
  formatTimeAgo,
  formatExactDate,
}) => {
  if (!notification) return null;

  const config = getNotificationConfig(notification.type);
  const IconComponent = config.icon;
  const timeAgo = formatTimeAgo ? formatTimeAgo(notification.createdOn) : "Recently";
  const exactDate = formatExactDate ? formatExactDate(notification.createdOn) : "";

  return (
    <Card
      onClick={onClick}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${isUnread ? colors.primary.light : colors.dashboard.cardBorder}`,
        boxShadow: isUnread ? "0 4px 14px rgba(37, 99, 235, 0.08)" : customShadows.cardFlat,
        cursor: "pointer",
        position: "relative",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: colors.primary.main,
          boxShadow: customShadows.cardHover,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
        {/* Left: Icon Avatar Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: borderRadiusPx.lg,
              bgcolor: config.iconBg,
              color: config.iconColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconComponent size={22} />
          </Box>
        </Box>

        {/* Center: Details Content */}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          {/* Header Row: Badge, Unread Dot & Timestamp */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.75, flexWrap: "wrap", gap: 1 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Category Badge */}
              <Box
                sx={{
                  px: 1.25,
                  py: 0.25,
                  borderRadius: borderRadiusPx.sm,
                  bgcolor: config.iconBg,
                  color: config.iconColor,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                }}
              >
                {config.badgeLabel}
              </Box>

              {/* Unread Indicator Dot */}
              {isUnread && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: borderRadiusPx.pill,
                    bgcolor: colors.primary.main,
                    boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.25)",
                  }}
                />
              )}
            </Stack>

            {/* Time Stamp with Clock Icon */}
            <Tooltip title={exactDate || timeAgo}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: colors.slate[500] }}>
                <Clock size={13} />
                <Typography variant="caption" sx={{ color: "inherit", fontWeight: 500 }}>
                  {timeAgo}
                </Typography>
              </Stack>
            </Tooltip>
          </Stack>

          {/* Title */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: colors.slate[900],
              fontSize: "0.95rem",
              lineHeight: 1.4,
              mb: 0.5,
            }}
          >
            {notification.title}
          </Typography>

          {/* Content Description */}
          <Typography
            variant="body2"
            sx={{
              color: colors.slate[600],
              fontSize: "0.875rem",
              lineHeight: 1.5,
              mb: 1.5,
              wordBreak: "break-word",
            }}
          >
            {notification.content}
          </Typography>

          {/* Bottom Row: Metadata Chips & Action Buttons */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            sx={{ gap: 1 }}
          >
            {/* Metadata Tags */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {notification.genId && (
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: borderRadiusPx.xs,
                    bgcolor: colors.slate[100],
                    color: colors.slate[700],
                    fontSize: "0.72rem",
                    fontWeight: 600,
                  }}
                >
                  ID: #{String(notification.genId).slice(-6)}
                </Box>
              )}
              {notification.lotteryName && (
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: borderRadiusPx.xs,
                    bgcolor: colors.blue[50],
                    color: colors.primary.main,
                    fontSize: "0.72rem",
                    fontWeight: 600,
                  }}
                >
                  {notification.lotteryName} BET
                </Box>
              )}
            </Stack>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1} alignItems="center">
              {onOpenDetails && (
                <Tooltip title="View full event payload">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDetails(notification);
                    }}
                    sx={{
                      color: colors.slate[400],
                      p: 0.5,
                      "&:hover": {
                        color: colors.slate[700],
                        bgcolor: colors.slate[100],
                      },
                    }}
                  >
                    <Info size={16} />
                  </IconButton>
                </Tooltip>
              )}

              <CommonButton
                variant="outline"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick(notification);
                }}
                endIcon={<ChevronRight size={14} />}
                sx={{
                  py: 0.5,
                  px: 1.5,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                }}
              >
                {config.actionLabel}
              </CommonButton>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
};

NotificationCard.propTypes = {
  notification: PropTypes.shape({
    _id: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    genId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lotteryName: PropTypes.string,
    createdOn: PropTypes.string,
    owner: PropTypes.string,
    user: PropTypes.string,
  }).isRequired,
  isUnread: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  onOpenDetails: PropTypes.func,
  formatTimeAgo: PropTypes.func,
  formatExactDate: PropTypes.func,
};

export default NotificationCard;
